import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Divider,
  Form,
  Button,
  Row,
  Col,
  Modal,
  Table,
  Avatar,
  Typography,
  Card,
  Grid,
} from "antd";
import { useUser } from "../../contexts/UserContext";
import { EditOutlined } from "@ant-design/icons";
import EditDeliveryAddressForm from "../../components/checkout/EditDeliveryAddressForm";
import axios from "axios";
import { apiEndpoint } from "../../constants/constants";

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

const ProductCheckout = () => {
  const location = useLocation();
  const { item } = location.state || { item: { product: {}, quantity: 0 } };
  const { currentUser } = useUser();
  const [visible, setVisible] = useState(false);
  const [address, setAddress] = useState(currentUser?.address);
  const [unitNumber, setUnitNumber] = useState(currentUser?.unitNumber);

  const screens = useBreakpoint();

  const totalPrice = (item?.quantity || 0) * (item?.product?.price || 0);

  const onFinish = async () => {
    const seller = item.product.seller._id;
    localStorage.setItem(
      "checkOutData",
      JSON.stringify({
        item,
        shippingAddress: address,
        unitNumber,
        totalPrice,
        cartCheckout: false,
        seller,
      }),
    );

    const response = await axios.post(
      `${apiEndpoint}/stripe/checkout-session`,
      {
        buyer: currentUser,
        seller,
        items: [item],
      },
    );

    window.location.href = response.data.url;
  };

  const columns = [
    {
      title: "Product",
      key: "product",
      render: () => (
        <Row align="middle" gutter={screens.xs ? 8 : 12} wrap={false}>
          <Col flex="none">
            <Avatar
              shape="square"
              src={`${item.product.images}?v=${item.product.imageVersion}`}
              size={screens.xs ? 40 : 64}
              style={{ borderRadius: "6px" }}
            />
          </Col>
          <Col flex="auto" style={{ minWidth: 0 }}>
            <Text
              strong
              style={{
                fontSize: screens.xs ? "13px" : "16px",
                display: "block",
                lineHeight: 1.2,
                wordBreak: "break-word",
              }}
            >
              {item.product.name}
            </Text>

            {/* Inline Qty & Price details on mobile */}
            {screens.xs && (
              <Text
                type="secondary"
                style={{
                  fontSize: "11px",
                  display: "block",
                  whiteSpace: "nowrap",
                }}
              >
                Qty: {item.quantity} × ${(item.product.price || 0).toFixed(2)}
              </Text>
            )}
          </Col>
        </Row>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      align: "center",
      responsive: ["sm"], // Hides column on mobile (xs)
    },
    {
      title: "Price",
      dataIndex: ["product", "price"],
      key: "price",
      align: "center",
      responsive: ["sm"], // Hides column on mobile (xs)
      render: (price) => `$${price.toFixed(2)}`,
    },
    {
      title: "Total",
      key: "total",
      align: "center",
      width: screens.xs ? 80 : undefined,
      render: (_, record) => (
        <Text
          strong
          style={{
            fontSize: screens.xs ? "13px" : "16px",
            whiteSpace: "nowrap", // Prevents price overflow/wrap
          }}
        >
          ${(record.quantity * record.product.price).toFixed(2)}
        </Text>
      ),
    },
  ];

  const dataSource = [
    { key: 1, product: item.product, quantity: item.quantity },
  ];

  return (
    <div
      style={{
        maxWidth: "900px",
        margin: "0 auto",
        padding: screens.xs ? "12px 8px" : "32px 16px",
      }}
    >
      <Title level={2} style={{ marginBottom: "20px" }}>
        Checkout
      </Title>

      {/* Address Card */}
      <Card
        style={{
          marginBottom: "20px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
        styles={{ body: { padding: screens.xs ? "12px" : "20px" } }}
      >
        <Row justify="space-between" align="middle" gutter={[12, 12]}>
          <Col xs={20} sm={21}>
            <Text
              type="secondary"
              style={{ fontSize: "12px", display: "block" }}
            >
              SHIPPING ADDRESS
            </Text>
            <Text strong style={{ fontSize: screens.xs ? "14px" : "16px" }}>
              {address} {unitNumber && `, Unit Number: ${unitNumber}`}
            </Text>
          </Col>
          <Col xs={4} sm={3} style={{ textAlign: "right" }}>
            <Button
              shape="circle"
              type="primary"
              ghost
              icon={<EditOutlined />}
              onClick={() => setVisible(true)}
            />
          </Col>
        </Row>
      </Card>

      {/* Table Container */}
      <Card
        styles={{ body: { padding: 0 } }}
        style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
      >
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          size={screens.xs ? "small" : "default"}
          scroll={{ x: true }}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell colSpan={screens.xs ? 1 : 3} align="right">
                <Text
                  strong
                  style={{
                    fontSize: screens.xs ? "13px" : "18px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Total Price:
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell align="center">
                <Text
                  strong
                  type="danger"
                  style={{
                    fontSize: screens.xs ? "14px" : "20px",
                    whiteSpace: "nowrap",
                  }}
                >
                  ${totalPrice.toFixed(2)}
                </Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      </Card>

      <Divider style={{ margin: "24px 0" }} />

      {/* Submit Button */}
      <Form name="checkout" layout="vertical" onFinish={onFinish}>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block={screens.xs}
            style={{ minWidth: "200px", float: screens.xs ? "none" : "right" }}
          >
            Proceed to Payment
          </Button>
        </Form.Item>
      </Form>

      <Modal
        open={visible}
        footer={false}
        closable={true}
        onCancel={() => setVisible(false)}
        title="Shipping Address"
        centered
      >
        <EditDeliveryAddressForm
          setAddress={setAddress}
          setUnitNumber={setUnitNumber}
          setVisible={setVisible}
        />
      </Modal>
    </div>
  );
};

export default ProductCheckout;
